<script lang="ts">
import {onNextFrame} from '../domutils.ts';
import {parseViewBoxString, basePercentToBox} from '../features/selectregion_coords.ts';

export type AllSpiceSnippetReferenceData = {
  url: string,
  refElm: SVGSVGElement | null,
  clickThruListener: EventListener | null,
  isVisible: boolean,
  selectChangeEventID: string,
  // value of the anchor query parameter
  currentAnchorID: string | null,
  // detaches the marker from the ViewBoxController's view changes (EcadDocument)
  viewChangeUnsub: (() => void) | null,
};

export default {
  name: 'AllSpiceSnippetReference',
  props: {
    referenceElement: {
      validator: (value) => {
        return value === null || value instanceof SVGSVGElement;
      },
      default: null,
    },
    panZoom: {
      type: Object,
      default: (): null => null,
    },
    // when used for a multipage document, isActive is only true for the page for which the clickthrough snippet exists
    isActive: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data(): AllSpiceSnippetReferenceData {
    const searchParams = new URLSearchParams(window.location.search);
    const currentAnchorID = searchParams.get('anchor');

    return {
      url: window.location.search,
      refElm: this.referenceElement,
      clickThruListener: null,
      isVisible: true,
      selectChangeEventID: '',
      currentAnchorID,
      viewChangeUnsub: null,
    };
  },
  watch: {
    url() {
      this.isVisible = true;
      this.setPosition();
    },
    // viewBox mode: no ancestor transform carries the marker, so reposition (no scroll) on every view change. No-op if not a ViewBoxController.
    panZoom: {
      immediate: true,
      handler(panZoom) {
        this.viewChangeUnsub?.();
        this.viewChangeUnsub = null;
        if (panZoom && typeof panZoom.onChange === 'function') {
          this.viewChangeUnsub = panZoom.onChange(() => this.setPosition({scroll: false}));
        }
      },
    },
  },
  mounted() {
    // update data when the url changes within design reviews
    this.clickThruListener = () => {
      // reset zoom so the right portions are selected
      this.panZoom?.reset();
      this.updateDataFromUrl();
      this.isVisible = true;
      onNextFrame(() => {
        this.setPosition();
      });
    };
    document.addEventListener('allspice-snippet-click-thru', this.clickThruListener);

    // hide the box when the user clicks the snippet button
    document.addEventListener('selectregionmodechange', (e) => {
      this.selectChangeEventID = (e.target as HTMLElement).id;
      if (this.selectChangeEventID !== this.currentAnchorID && this.selectChangeEventID !== '') return;

      const detail = (e as CustomEvent<{isSelectMode: boolean}>).detail;
      if (detail.isSelectMode) {
        this.isVisible = false;
      } else {
        this.isVisible = true;
      }
    });

    // recalculate the position when the window is resized
    // eslint-disable-next-line github/prefer-observers
    window.addEventListener('resize', () => {
      this.setPosition();
    });

    // for an ecad document, most snippet references are inactive
    if (this.isActive) {
      this.updateDataFromUrl();
      this.setPosition();
    }
  },
  beforeUnmount() {
    document.removeEventListener('allspice-snippet-click-thru', this.clickThruListener);
    this.viewChangeUnsub?.();
  },
  updated() {
    this.setPosition();
  },
  methods: {
    getQueryParams() {
      const queryString = window.location.search;
      return new URLSearchParams(queryString);
    },
    updateDataFromUrl() {
      const anchor = this.getQueryParams().get('anchor');
      if (anchor && !this.refElm) {
        this.refElm = document.querySelector(`#${anchor} .schematic_svg`) || document.querySelector(`#${anchor} .schematic`);
      }
      this.url = window.location.search;
    },
    dismissBox() {
      this.isVisible = false;

      // clear the query params
      const queryParams = this.getQueryParams();
      queryParams.delete('anchor');
      queryParams.delete('doc-id');
      queryParams.delete('view-coordinates');
      queryParams.delete('layers');

      const base = window.location.pathname;
      const search = queryParams.toString();
      const hash = window.location.hash;
      const newUrl = base + (search ? `?${search}` : '') + hash;
      window.history.replaceState({}, '', newUrl);
    },
    setPosition({scroll = true}: {scroll?: boolean} = {}) {
      if (this.selectChangeEventID !== this.currentAnchorID && this.selectChangeEventID !== '') return;

      if (!this.isVisible) return;

      const docId = this.getQueryParams().get('doc-id');
      // only show the box if the snippet is within the accordion
      if (docId && !this.$el.closest(`[data-accordion-content="${CSS.escape(docId)}"]`)) {
        this.isVisible = false;
      }

      if (!this.refElm) return;
      // don't position when the reference element doesn't match (design reviews, accordions)
      if (this.refElm.id !== this.$el.previousElementSibling?.id) {
        this.isVisible = false;
        return;
      }

      const coordinates = this.getQueryParams().get('view-coordinates').split(',').map((c) => Number(c) / 100);
      onNextFrame(() => {
        this.setStyles(this.refElm, coordinates, this.$el);
        // Only scroll on an explicit show, not while re-tracking a live zoom/pan. Scroll the box (a plain HTML div), not the inline svg — WebKit scrolls svg unreliably.
        if (scroll) this.$el.scrollIntoView({block: 'center', inline: 'nearest', behavior: 'instant'});
      });
    },
    setStyles(element: Element, coordinates: number[], box: HTMLElement) {
      const [left, top, right, bottom] = coordinates;
      const bounds = element.getBoundingClientRect();
      const {width} = bounds;

      let boxLeft: number;
      let boxTop: number;
      let boxWidth: number;
      let boxHeight: number;

      // viewBox mode (EcadDocument): map the saved %-of-document through the current CTM; plain %×box is only right at fit.
      // Legacy transform mode (AllSpiceDiffView): keep %×box — an ancestor CSS transform scales the marker.
      const base = parseViewBoxString(element.getAttribute('data-base-viewbox'));
      const ctm = element instanceof SVGGraphicsElement ? element.getScreenCTM() : null;
      if (base && ctm) {
        ({left: boxLeft, top: boxTop, width: boxWidth, height: boxHeight} =
          basePercentToBox({left, top, right, bottom}, ctm, base, {left: bounds.left, top: bounds.top}));
      } else {
        const {height} = bounds;
        boxLeft = width * left;
        boxTop = height * top;
        boxWidth = width * right - boxLeft;
        boxHeight = height * bottom - boxTop;
      }

      // If the box is too close to the right edge of the screen, move the close button to the left
      if (boxLeft + boxWidth + 10 > width) {
        box.style.setProperty('--snippet-close-pos', '-26px');
      } else {
        box.style.setProperty('--snippet-close-pos', 'calc(100% + 5px)');
      }
      box.style.left = `${boxLeft}px`;
      box.style.top = `${boxTop}px`;
      box.style.width = `${boxWidth}px`;
      box.style.height = `${boxHeight}px`;
      box.querySelector<HTMLElement>('.svg-child').style.width = `${boxWidth}px`;
      box.querySelector<HTMLElement>('.svg-child').style.height = `${boxHeight}px`;
    },
  },
};
</script>
<template>
  <div
    v-show="isVisible"
    class="snippet-coordinates-box"
    data-testid="snippet-coordinates-box"
  >
    <svg class="svg-child">
      <rect
        x="0"
        y="0"
        width="100%"
        height="100%"
        fill="none"
      />
    </svg>
    <div class="utils">
      <button
        class="ui button selection-region-button"
        @click="dismissBox"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 16 16"
          width="16"
          height="16"
          class="svg"
        >
          <path
            d="M3.72 3.72a.75.75 0 0 1 1.06 0L8 6.94l3.22-3.22a.749.749 0 0 1 1.275.326.749.749 0 0 1-.215.734L9.06 8l3.22 3.22a.749.749 0 0 1-.326 1.275.749.749 0 0 1-.734-.215L8 9.06l-3.22 3.22a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042L6.94 8 3.72 4.78a.75.75 0 0 1 0-1.06Z"
          />
        </svg>
      </button>
    </div>
  </div>
</template>
<style scoped>
.snippet-coordinates-box {
  --snippet-close-pos: calc(100% + 5px);
  position: absolute;
  pointer-events: none;
}

.selection-region-button {
  pointer-events: initial;
}

.utils {
  left: var(--snippet-close-pos);
  position: absolute;
  top: 0;
}

@keyframes dash {
  to {
    stroke-dashoffset: 0;
  }
}

@keyframes pulse {
  0%,
  100% {
    fill: transparent;
  }
  50% {
    fill: var(--color-primary-alpha-30);
  }
}

rect {
  animation: dash 2s linear forwards, pulse 1s ease 1.5s 2;
  stroke-dasharray: 1500;
  stroke-dashoffset: 1500;
  stroke-width: 6;
  stroke: var(--color-primary);
  will-change: stroke-dashoffset;
}
</style>
